// The comms wizard's email steps after the business name (spec E2 PR 4 item
// 6): email-domain, which says the address emails come from is set up in the
// Your domain card and opens it, and email-4 (Email Ready). `deps` carries
// what they use from app.js's scope.
//
// ⚠️ THE DOMAIN LIVES IN ONE PLACE. The wizard's own domain and verify steps
// (email-2, email-3) registered a second way into the same Amazon identity,
// with their own records table and their own "up to an hour"; the Your domain
// card is the one place now.
import { buildCancelAddress, isBrandedCancelAddress, inboundDomainOf } from './cancel-address.js';
import { emailDomainVerified } from './email-domain-status.js';

/**
 * @param {any} deps closeCommsWizard
 * @returns {any} the email-domain step object
 */
export function emailDomainStep(deps) {
  const { closeCommsWizard } = deps;
  return {
    id: 'email-domain', title: 'Your domain', render: () => `
      <p class="comms-modal-desc">The address your emails come from is set up in Your domain.</p>
      <div style="margin-top:16px;"><button class="btn-secondary" id="wiz-open-your-domain">Open Your domain</button></div>`,
    onMount: () => {
      document.getElementById('wiz-open-your-domain')?.addEventListener('click', () => {
        closeCommsWizard();
        document.getElementById('your-domain-card')?.scrollIntoView({ block: 'start', behavior: 'smooth' });
      });
    },
  };
}

/**
 * @param {any} deps loadSettings, escHtml, _config
 * @returns {any} the email-4 step object
 */
export function emailReadyStep(deps) {
  const { loadSettings, escHtml, _config } = deps;
  return (
  { id: 'email-4', title: 'Email Ready', render: () => {
    const settings = loadSettings();
    // ⚠️ NEVER notifications@yourdomain.com. Until Your domain is verified,
    // emails go out from FitOps's own address with the coach's business name.
    const verified = emailDomainVerified(settings);
    const domain = verified ? String(settings.emailDomain).trim() : 'fitopspro.com';
    // ⚠️ ONE SOURCE OF TRUTH. This screen is captioned "Put this in your
    // client agreements", so whatever it prints ends up in a contract. It
    // used to be a hardcoded platform address — a domain the coach's
    // clients have never heard of, in the coach's own white-labelled
    // agreement, two boxes below an outbound address that WAS branded.
    const cancelAddr = buildCancelAddress(settings);
    const cancelBranded = isBrandedCancelAddress(settings);
    // The host replies come back on: send-email's replyDomainFor rule, the
    // coach's own reply.<domain> once it receives (11/1).
    const inbound = inboundDomainOf(settings) || '';
    const replyHost = cancelBranded && inbound.startsWith('reply.') ? inbound : 'reply.fitopspro.com';
    const cancelJs = cancelAddr.replace(/'/g, "\\'");
    return (verified ? `
      <div style="text-align:center;padding:10px 0 6px;">
        <div style="font-size:32px;color:#22c55e;margin-bottom:8px;">&#10003;</div>
        <p style="font-size:16px;font-weight:700;color:var(--text);margin-bottom:16px;">Email is set up.</p>
      </div>
      <p class="comms-modal-desc" style="margin-bottom:16px;">Your emails will go out from your verified domain. Replies come back into your FitOps Pro inbox.</p>`
      : `
      <p class="comms-modal-desc" style="margin-bottom:16px;">Until Your domain is set up, your emails come from notifications@fitopspro.com, with your business name on them.</p>`) + `
      <div style="display:flex;flex-direction:column;gap:10px;">
        <div style="background:var(--surface-2);border:1px solid var(--border);border-radius:var(--radius-sm);padding:12px 14px;">
          <div style="font-size:10px;font-weight:600;color:var(--text-muted);letter-spacing:0;margin-bottom:4px;">Outbound notifications</div>
          <div style="font-size:14px;font-weight:600;color:var(--text);cursor:pointer;" title="Click to copy" onclick="navigator.clipboard.writeText('notifications@${escHtml(domain)}');this.textContent='Copied!';this.style.color='var(--success)';setTimeout(()=>{this.textContent='notifications@${escHtml(domain)}';this.style.color=''},1200)">notifications@${escHtml(domain)}</div>
          <div style="font-size:11px;color:var(--text-muted);margin-top:2px;">Receipts, payment failures, alerts</div>
        </div>
        <div style="background:var(--surface-2);border:1px solid var(--border);border-radius:var(--radius-sm);padding:12px 14px;">
          <div style="font-size:10px;font-weight:600;color:var(--text-muted);letter-spacing:0;margin-bottom:4px;">Client replies</div>
          <div style="font-size:14px;font-weight:600;color:var(--text);">Automatic — replies appear in your CRM inbox</div>
          <div style="font-size:11px;color:var(--text-muted);margin-top:2px;">Routed via ${escHtml(replyHost)} and managed by FitOps Pro. Your clients never see it.</div>
        </div>
        <div style="background:rgba(245,158,11,0.06);border:1px solid rgba(245,158,11,0.2);border-radius:var(--radius-sm);padding:12px 14px;">
          <div style="font-size:10px;font-weight:600;color:#f59e0b;letter-spacing:0;margin-bottom:4px;">Cancellation requests</div>
          <div style="font-size:14px;font-weight:600;color:var(--text);cursor:pointer;" title="Click to copy" onclick="navigator.clipboard.writeText('${cancelJs}');this.textContent='Copied!';this.style.color='var(--success)';setTimeout(()=>{this.textContent='${cancelJs}';this.style.color=''},1200)">${escHtml(cancelAddr)}</div>
          <div style="font-size:11px;color:var(--text-muted);margin-top:2px;">${cancelBranded
            ? 'Put this in your client agreements. It is on your own domain. A request here reaches you straight away and nothing is cancelled until you say so.'
            : 'Put this in your client agreements. A request here reaches you straight away and nothing is cancelled until you say so.'}</div>
        </div>
      </div>
      <div style="margin-top:18px;padding-top:14px;border-top:1px solid var(--border);">
        <div style="font-size:13px;font-weight:600;color:var(--text);margin-bottom:8px;">Send yourself a test</div>
        <div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;">
          <input type="email" id="wiz-test-email-to" placeholder="you@example.com" value="${escHtml(loadSettings().emailReplyTo || '')}" style="flex:1;min-width:200px;" />
          <button class="btn-secondary" id="wiz-test-email-btn" style="white-space:nowrap;">Send test</button>
        </div>
        <div id="wiz-test-email-result" style="margin-top:8px;font-size:12px;"></div>
      </div>`;
  },
    onMount: () => {
      document.getElementById('wiz-test-email-btn')?.addEventListener('click', async () => {
        const to = document.getElementById('wiz-test-email-to')?.value?.trim();
        const resultEl = document.getElementById('wiz-test-email-result');
        if (!to) { resultEl.innerHTML = '<span style="color:var(--danger);">Enter an email address.</span>'; return; }
        const btn = document.getElementById('wiz-test-email-btn');
        btn.disabled = true;
        btn.textContent = 'Sending…';
        try {
          const supabase = _config.getSupabase();
          const { data: { session } } = await supabase.auth.getSession();
          const res = await fetch('/.netlify/functions/ses-domain-setup', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + session.access_token },
            body: JSON.stringify({
              action: 'test-email',
              to,
              subject: 'FitOps Pro — Email setup test',
              bodyText: 'This is a test from your FitOps Pro email setup. If you got this, outbound is working from your verified domain.',
            }),
          });
          const json = await res.json();
          if (!res.ok) throw new Error(json.error);
          resultEl.innerHTML = '<span style="color:var(--success);">Sent. Check ' + escHtml(to) + ' (give it ~30 sec).</span>';
        } catch (err) {
          resultEl.innerHTML = '<span style="color:var(--danger);">' + escHtml(err.message) + '</span>';
        } finally {
          btn.disabled = false;
          btn.textContent = 'Send test';
        }
      });
    },
  }
  );
}
